/**
 * Чтение административных ответов сервера в типы интерфейса.
 *
 * Зачем слой: несколько административных ручек отдают больше и иначе, чем
 * описано в `lib/api.ts` (например `/api/enums` — массив наборов с именем и
 * признаком защиты, `/api/users` — e-mail и дату последнего входа,
 * `/api/settings` — значения вместе с подписями и списком секретов).
 * Мы не правим общий клиент и не подменяем типы приведением: ответ читается
 * как `unknown` через узкие проверки ниже. Когда клиент догонит сервер,
 * этот файл сократится до реэкспорта.
 */
import type { ChannelCode, EnumValue, PermissionCode, UserSummary } from '@shared/types.ts'
import { api, ApiRequestError } from '../../lib/api.js'

// ───────────────────────────── узкие проверки ─────────────────────────────

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

export function readString(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

export function readNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function readBool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

/** Строки из массива, всё остальное отбрасываем. */
function readStrings(value: unknown): string[] {
  const out: string[] = []
  for (const item of readArray(value)) {
    const text = readString(item)
    if (text !== null) out.push(text)
  }
  return out
}

// ───────────────────────────── справочники значений ─────────────────────────

export interface EnumSetView {
  code: string
  name: string
  hint: string | null
  /** Набор защищён: значения нельзя удалять, только переименовывать и переставлять. */
  isLocked: boolean
  values: EnumValue[]
}

/** Имена наборов на случай, если сервер пришлёт только значения (§9.2). */
const SET_NAMES: Record<string, string> = {
  tier: 'Приоритет (тир)',
  visibility: 'Видимость',
  stock_display: 'Отображаемый статус наличия',
  oos: 'Статус «нет в наличии»',
  seo_priority: 'Приоритет SEO',
  context_priority: 'Приоритет Контекст',
}

/** Наборы, защищённые от удаления значений: на них стоит сортировка (§9.2). */
const LOCKED_SETS = new Set(['tier', 'visibility'])

function readEnumValue(raw: unknown, setCode: string, index: number): EnumValue | null {
  if (!isRecord(raw)) return null
  const id = readNumber(raw.id)
  const value = readString(raw.value)
  if (id === null || value === null) return null
  return {
    id,
    setCode: readString(raw.setCode) ?? setCode,
    value,
    label: readString(raw.label) ?? value,
    color: readString(raw.color),
    hint: readString(raw.hint),
    sortOrder: readNumber(raw.sortOrder) ?? index + 1,
    isActive: readBool(raw.isActive, true),
  }
}

function readEnumSet(raw: unknown, codeFromKey?: string): EnumSetView | null {
  if (!isRecord(raw)) return null
  const code = readString(raw.code) ?? codeFromKey ?? null
  if (code === null) return null
  const values = readArray(raw.values)
    .map((item, index) => readEnumValue(item, code, index))
    .filter((item): item is EnumValue => item !== null)
  return {
    code,
    name: readString(raw.name) ?? SET_NAMES[code] ?? code,
    hint: readString(raw.hint),
    isLocked: readBool(raw.isLocked, LOCKED_SETS.has(code)),
    values: values.sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id),
  }
}

/**
 * Наборы значений. Сервер отдаёт `{ sets: [...] }`, но исторически возможна и
 * форма «код набора → значения» — читаем обе и приводим к одной.
 */
export async function loadEnumSets(): Promise<EnumSetView[]> {
  const raw: unknown = await api.enums()
  const sets = isRecord(raw) ? raw.sets : null

  if (Array.isArray(sets)) {
    return sets
      .map((item) => readEnumSet(item))
      .filter((item): item is EnumSetView => item !== null)
  }

  if (isRecord(sets)) {
    const out: EnumSetView[] = []
    for (const [code, values] of Object.entries(sets)) {
      const set = readEnumSet({ code, values }, code)
      if (set) out.push(set)
    }
    return out
  }

  return []
}

// ───────────────────────────── пользователи и роли ──────────────────────────

/** Пользователь в административном списке: сверх UserSummary — вход и почта. */
export interface AdminUserView extends UserSummary {
  email: string
  roleId: number | null
  mustChangePassword: boolean
  /** Код входа: его видит и пересылает администратор. */
  loginCode: string | null
  /** Служебная учётка для проверок — в списке скрыта по умолчанию. */
  isService: boolean
  lastLoginAt: string | null
}

function readUser(raw: unknown): AdminUserView | null {
  if (!isRecord(raw)) return null
  const id = readNumber(raw.id)
  const fullName = readString(raw.fullName)
  if (id === null || fullName === null) return null
  return {
    id,
    fullName,
    shortName: readString(raw.shortName) ?? fullName,
    roleCode: readString(raw.roleCode) ?? '',
    roleName: readString(raw.roleName) ?? '',
    area: readString(raw.area),
    jobTitle: readString(raw.jobTitle),
    isActive: readBool(raw.isActive, true),
    color: readString(raw.color),
    email: readString(raw.email) ?? '',
    roleId: readNumber(raw.roleId),
    mustChangePassword: readBool(raw.mustChangePassword, false),
    loginCode: readString(raw.loginCode),
    isService: readBool(raw.isService, false),
    lastLoginAt: readString(raw.lastLoginAt),
  }
}

export async function loadUsers(): Promise<AdminUserView[]> {
  const raw: unknown = await api.users()
  const items = isRecord(raw) ? readArray(raw.items) : []
  return items.map(readUser).filter((item): item is AdminUserView => item !== null)
}

export interface RoleView {
  id: number
  code: string
  name: string
  area: string | null
  isSystem: boolean
  usersCount: number
  permissions: string[]
}

export interface PermissionView {
  code: string
  name: string
  group: string
}

export interface RolesView {
  roles: RoleView[]
  permissions: PermissionView[]
}

function readRole(raw: unknown): RoleView | null {
  if (!isRecord(raw)) return null
  const id = readNumber(raw.id)
  const code = readString(raw.code)
  if (id === null || code === null) return null
  return {
    id,
    code,
    name: readString(raw.name) ?? code,
    area: readString(raw.area),
    isSystem: readBool(raw.isSystem, code === 'owner' || code === 'admin'),
    usersCount: readNumber(raw.usersCount) ?? 0,
    permissions: readStrings(raw.permissions),
  }
}

function readPermission(raw: unknown): PermissionView | null {
  if (!isRecord(raw)) return null
  const code = readString(raw.code)
  if (code === null) return null
  return { code, name: readString(raw.name) ?? code, group: readString(raw.group) ?? '' }
}

export async function loadRoles(): Promise<RolesView> {
  const raw: unknown = await api.roles()
  if (!isRecord(raw)) return { roles: [], permissions: [] }
  return {
    roles: readArray(raw.items).map(readRole).filter((item): item is RoleView => item !== null),
    permissions: readArray(raw.permissions)
      .map(readPermission)
      .filter((item): item is PermissionView => item !== null),
  }
}

// ───────────────────────────────── настройки ────────────────────────────────

export interface SettingsView {
  /** Значение по ключу настройки. Секреты приходят маской, а не значением. */
  values: Record<string, unknown>
  /** Подписи ключей с сервера — показываем их в сообщениях об ошибках. */
  labels: Record<string, string>
  /** Ключи, которые сервер никогда не отдаёт целиком. */
  secretKeys: string[]
}

function readSettingsView(raw: unknown): SettingsView {
  if (!isRecord(raw)) return { values: {}, labels: {}, secretKeys: [] }
  // `values` — текущая форма ответа, `settings` — форма из типов клиента.
  const values = isRecord(raw.values) ? raw.values : isRecord(raw.settings) ? raw.settings : {}
  const labelsRaw = isRecord(raw.labels) ? raw.labels : {}
  const labels: Record<string, string> = {}
  for (const [key, value] of Object.entries(labelsRaw)) {
    const text = readString(value)
    if (text !== null) labels[key] = text
  }
  return { values, labels, secretKeys: readStrings(raw.secretKeys) }
}

export async function loadSettings(): Promise<SettingsView> {
  const raw: unknown = await api.settings()
  return readSettingsView(raw)
}

export async function saveSettings(patch: Record<string, unknown>): Promise<SettingsView> {
  const raw: unknown = await api.updateSettings(patch)
  return readSettingsView(raw)
}

// ─────────────────────────── состояние уведомлений ──────────────────────────

export interface TelegramStatusView {
  enabled: boolean
  configured: boolean
  chatTitle: string | null
  lastError: string | null
  pendingCount: number
  failedCount: number
}

export async function loadTelegramStatus(): Promise<TelegramStatusView> {
  const raw: unknown = await api.telegramStatus()
  if (!isRecord(raw)) {
    return { enabled: false, configured: false, chatTitle: null, lastError: null, pendingCount: 0, failedCount: 0 }
  }
  return {
    enabled: readBool(raw.enabled, false),
    configured: readBool(raw.configured, false),
    chatTitle: readString(raw.chatTitle),
    lastError: readString(raw.lastError),
    pendingCount: readNumber(raw.pendingCount) ?? 0,
    failedCount: readNumber(raw.failedCount) ?? 0,
  }
}

// ─────────────────────── архивация категории с привязками ───────────────────

/**
 * Архивация категории, у которой есть живые привязки.
 *
 * Сервер в этом случае требует `{ force: true }` (§9.1), а `api.archiveCategory`
 * тело запроса не принимает — поэтому запрос собран здесь по тем же правилам:
 * заголовок X-Requested-With, cookie сессии, ошибка в ApiRequestError.
 * ПРОСЬБА К ВЕДУЩЕМУ: добавить в `lib/api.ts` параметр
 * `archiveCategory(id, force?)` и удалить эту функцию.
 */
export async function archiveCategoryForce(id: number): Promise<void> {
  const res = await fetch(`/api/categories/${id}/archive`, {
    method: 'POST',
    headers: { 'X-Requested-With': 'bm', 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ force: true }),
  })
  if (res.ok) return

  const text = await res.text()
  const parsed: unknown = text ? JSON.parse(text) : null
  const error = isRecord(parsed) && isRecord(parsed.error) ? parsed.error : null
  const message = (error && readString(error.message)) ?? 'Сервер не ответил. Проверьте соединение.'
  throw new ApiRequestError(res.status, { code: 'validation', message })
}

// ───────────────────────────── общие помощники ──────────────────────────────

/** Текст ошибки для тоста: у ApiRequestError он уже по-русски. */
export function errorText(err: unknown): string {
  if (err instanceof ApiRequestError) return err.message
  if (err instanceof Error) return err.message
  return 'Не удалось выполнить действие'
}

/** Сервер просит подтверждения: в details лежит число брендов (§9.1). */
export function needsConfirmation(err: unknown): number | null {
  if (!(err instanceof ApiRequestError)) return null
  const details = err.details
  if (!isRecord(details) || details.needsConfirmation !== true) return null
  return readNumber(details.brands) ?? 0
}

/** «14.10.2026, 15:02» — одинаково на всех административных экранах. */
export function formatDateTime(iso: string | null): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

/** Код канала в подпись: «Розница» / «Опт». */
export const CHANNEL_NAMES: Record<ChannelCode, string> = { retail: 'Розница', b2b: 'Опт' }

/** Есть ли у текущего пользователя право. */
export function hasPermission(permissions: readonly PermissionCode[] | undefined, code: PermissionCode): boolean {
  return (permissions ?? []).includes(code)
}
